import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dayIdLog } from '../lib/dayIdLog';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import {
  type Supplement,
  type SupplementDay,
  DEFAULT_STACK,
  DEFAULT_SUPPLEMENT_ICON,
  sumSupplementXP,
  supplementStreak,
  adherencePct,
} from '../utils/supplements';
import { amountOn, countedDoses, dayDoses, dayTimesError, extraKey, keyOfItem, markOf, mergeIntakeRecord, pauseItem, readDayTimes, resumeItem, DEFAULT_DAY_TIMES, SKIP_PREFIX, type DayTimes, type Dose, type DoseMark } from '../utils/intakeSchedule';
import { toLocalYMD, shiftYMD } from '../utils/hydration';

const HISTORY_DAYS = 60;
/** How far back the record is read at most — a supply counted from longer ago reads "unknown". */
const SUPPLY_HISTORY_DAYS = 366;

interface SupplementRow {
  log_date: string;
  taken_ids: string[] | null;
  scheduled: number | null;
}

function rowToDay(row: SupplementRow): SupplementDay {
  return { date: row.log_date, takenIds: Array.isArray(row.taken_ids) ? row.taken_ids : [], scheduled: row.scheduled ?? 0 };
}

interface SupplementStore {
  stack: Supplement[];
  /**
   * ⭐ The stack was SAVED (Growth Foundations' "Add your supplements", 2026-10-30): the column
   * held a list when it loaded, or this tab has written it since. ⚠️ False while the store shows
   * the STARTER EXAMPLES (`DEFAULT_STACK` — a never-saved column decodes to them): seeded examples
   * are not the person's list.
   */
  stackSaved: boolean;
  todayDate: string;
  todayTakenIds: string[];
  /** Doses due today (the schedule's count — `countedDoses` — stored with each change). */
  todayScheduled: number;
  history: SupplementDay[];
  /** The earliest day the record was read from — a supply counted from before it is unknown. */
  historyFrom: string;
  /**
   * ⭐ Where the record starts (`loadRecordStart`): the earliest saved day with something due and
   * marked, else today — `historyFrom` when the record began before the days loaded. A day before
   * it is BEFORE the record — never "not recorded", never counted.
   */
  recordStart: string;
  /** The person's usual day (wake, meals, bed) — where each dose's anchor falls. */
  dayTimes: DayTimes;
  /** The person saved their day (`intake_day_times`) — false: the defaults stand in ("Set your day"). */
  dayTimesSaved: boolean;

  isLoading: boolean;
  /** The last load failed (Home's widget offers Try again instead of waiting forever). */
  loadFailed: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  /** Re-read today's row (the tab came back — lib/dayLogFocusRefresh). Read only. */
  refreshToday: () => Promise<void>;
  toggleTaken: (id: string) => Promise<void>;
  /** Mark one of today's doses taken, skipped, or neither (utils/intakeSchedule). True when saved. */
  markDose: (key: string, mark: DoseMark) => Promise<boolean>;
  /** Log a one-off dose of an item today (it counts for supply, not adherence). True when saved. */
  logExtra: (itemId: string) => Promise<boolean>;
  /** "Restocked": the count on hand as of today. */
  restock: (id: string, count: number) => void;
  /** Save the person's usual day (mig 0304). False — nothing changed — when it can't be saved. */
  setDayTimes: (d: DayTimes) => Promise<boolean>;
  /** Add a supplement; returns its new id. */
  addSupplement: (s: Omit<Supplement, 'id' | 'active'>) => string;
  updateSupplement: (id: string, patch: Partial<Omit<Supplement, 'id'>>) => void;
  deleteSupplement: (id: string) => void;
  toggleActive: (id: string) => void;

  getSupplementXP: () => number;
  getStreak: () => number;
  getTodayAdherence: () => { taken: number; scheduled: number; pct: number };
  /** Today's counted doses (`countedDoses`: the due ones, and one marked before its item was paused), in the day's order. */
  getTodayDoses: () => Dose[];
  clearStore: () => void;
}

/**
 * How many doses `date` counts with the record `ids` — what the day's record stores as `scheduled`:
 * the doses due, and one marked before its item was paused that day (`countedDoses` — the one rule).
 */
const dueCount = (stack: Supplement[], date: string, ids: readonly string[]): number => countedDoses(stack, date, ids, useSupplementStore.getState().dayTimes).length;

function withTodayInHistory(history: SupplementDay[], today: SupplementDay): SupplementDay[] {
  const others = history.filter(d => d.date !== today.date);
  return [...others, today].sort((a, b) => a.date.localeCompare(b.date));
}

/** Today's taken_ids: one id on or off in the SERVER's list (lib/dayIdLog). */
const dayLog = dayIdLog({
  table: 'supplement_logs',
  column: 'taken_ids',
  what: 'supplement',
  scheduled: (ids) => dueCount(useSupplementStore.getState().stack, toLocalYMD(new Date()), ids),
  show: (date, ids) => {
    const s = useSupplementStore.getState();
    const scheduled = dueCount(s.stack, date, ids);
    useSupplementStore.setState({ todayDate: date, todayTakenIds: ids, todayScheduled: scheduled, history: withTodayInHistory(s.history, { date, takenIds: ids, scheduled }), recordStart: recordStartOn(s, date) });
  },
  loadedFor: () => useSupplementStore.getState().initializedUserId,
});

/**
 * The stack is `user_preferences.supplement_stack`, merged per supplement on
 * every write (lib/preferenceSync.ts) — a stale tab can no longer put back a
 * supplement deleted elsewhere or drop one added there.
 * ⚠️ An EMPTY saved stack is the user's choice: only a never-saved one (null)
 * starts from the defaults. Deleting every supplement used to bring them back.
 */
const stackSync = createPreferenceSync<Supplement[]>({
  column: 'supplement_stack',
  // ⭐ a record changed on both sides keeps this tab's copy — its PAUSES merged (`mergeIntakeRecord`): a
  // stale tab never moves a pause's start or undoes a Resume it never saw (polish round, fix 1)
  rule: { records: 'id', of: mergeIntakeRecord },
  decode: (saved) => (Array.isArray(saved) ? (saved as Supplement[]) : DEFAULT_STACK.map(s => ({ ...s }))),
  read: () => useSupplementStore.getState().stack,
  // ⚠️ A list the cloud holds that differs from this tab's: another tab or device SAVED one.
  adopt: (stack) => {
    applyStack(useSupplementStore.setState, useSupplementStore.getState, stack);
    useSupplementStore.setState({ stackSaved: true });
  },
  label: 'supplement stack',
  loadedFor: () => useSupplementStore.getState().initializedUserId,
});

/** Await the pending stack write (tests). */
export const flushSupplementStack = (): Promise<void> => stackSync.flush();

export const useSupplementStore = create<SupplementStore>((set, get) => ({
  stack: DEFAULT_STACK.map(s => ({ ...s })),
  stackSaved: false,
  todayDate: toLocalYMD(new Date()),
  todayTakenIds: [],
  todayScheduled: dayDoses(DEFAULT_STACK, toLocalYMD(new Date())).length, // the default day (the store isn't there yet)
  history: [],
  historyFrom: toLocalYMD(new Date()),
  recordStart: toLocalYMD(new Date()),
  dayTimes: DEFAULT_DAY_TIMES,
  dayTimesSaved: false,

  isLoading: false,
  loadFailed: false,
  isInitialized: false,
  initializedUserId: null,

  initialize: () => dayLog.load(async () => {
    const at = dayLog.generation();
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== dayLog.generation()) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true, loadFailed: false });
    try {
      const prefs = await fetchUserPreferences();
      // ⚠️ A failed read is not a never-saved list: the starter examples would stand in for the
      // person's own, and the day for the default one. The load fails (as Hydration's does).
      if (!prefs || prefs.failed) throw new Error('Could not read the supplement list');

      const todayStr = toLocalYMD(new Date());
      // Back far enough to count each supply from the day it was counted (a year at most).
      const since = supplySince(prefs?.row?.supplement_stack, shiftYMD(todayStr, -HISTORY_DAYS), shiftYMD(todayStr, -SUPPLY_HISTORY_DAYS));
      const { data, error } = await supabase
        .from('supplement_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      // Signed out while reading: the next account must not get this one's ticks — nor its
      // list as the base (so the list is loaded only after this).
      if (at !== dayLog.generation()) return;
      if (error) throw error;
      const rows = (data ?? []) as SupplementRow[];
      const recordStart = await loadRecordStart('supplement_logs', user.id, rows, since, todayStr);
      if (at !== dayLog.generation()) return;
      const stack = stackSync.load(prefs?.row?.supplement_stack ?? null, prefs?.row?.updated_at);
      // Before the stack's counts: they place each dose on the person's day.
      set({ dayTimes: readDayTimes(prefs?.row?.intake_day_times), dayTimesSaved: dayTimesWereSaved(prefs?.row?.intake_day_times) });

      const history = rows.map(rowToDay);
      const todayRow = rows.find(r => r.log_date === todayStr);
      const todayTakenIds = Array.isArray(todayRow?.taken_ids) ? (todayRow!.taken_ids as string[]) : [];
      // Today's due count follows the CURRENT list (today is live).
      const todayScheduled = dueCount(stack, todayStr, todayTakenIds);

      set({
        stack,
        // A list in the column — even an empty one — was saved; null is the starter examples.
        stackSaved: Array.isArray(prefs?.row?.supplement_stack),
        todayDate: todayStr,
        todayTakenIds,
        todayScheduled,
        history: withTodayInHistory(history, { date: todayStr, takenIds: todayTakenIds, scheduled: todayScheduled }),
        historyFrom: since,
        recordStart,
        isLoading: false,
        loadFailed: false,
        isInitialized: true,
        initializedUserId: user.id,
      });
      dayLog.loaded(todayStr, todayTakenIds);
    } catch (err) {
      if (at !== dayLog.generation()) return; // the next account's load may be running: leave its flag and base alone
      console.error('Error initializing supplements:', err);
      stackSync.reset(); // the list was never adopted — its base must not stand either
      set({ isLoading: false, loadFailed: true });
    }
  }),

  refreshToday: () => dayLog.refresh(),

  // ⚠️ Flips what THIS screen shows — applied to the server's list, so a stale screen can
  // only ever set the one id the user tapped (never re-send or drop the others).
  toggleTaken: async (id: string) => {
    await dayLog.set(id, !get().todayTakenIds.includes(id));
  },

  markDose: (key, mark) => dayLog.mark(key, mark),

  // ⚠️ The key is chosen against what THIS screen shows; two one-offs of the same item from two
  // stale tabs could share a key — the second is then a no-op (one dose recorded, never two).
  logExtra: (itemId) => dayLog.mark(extraKey(get().todayTakenIds, itemId), 'taken'),

  // ⚠️ A count is "on hand before that day's doses" (utils/intakeSchedule): what was taken today
  // is added back, so "I have 60 now" after lunch's dose still reads 60 — not 58.
  restock: (id, count) => {
    const { stack, todayDate, todayTakenIds } = get();
    const item = stack.find(s => s.id === id);
    if (!item) return;
    const takenToday = todayTakenIds.filter(k => !k.startsWith(SKIP_PREFIX) && keyOfItem(k, id)).length * amountOn(item, todayDate);
    const n = Math.max(0, Math.round(count)) + takenToday;
    persistStack(set, get, stack.map(s => (s.id === id ? { ...s, supply: { count: n, asOf: todayDate } } : s)));
  },

  addSupplement: (s) => {
    const full: Supplement = {
      ...s,
      id: crypto.randomUUID(),
      name: s.name.trim() || 'Supplement',
      dose: s.dose.trim(),
      icon: s.icon || DEFAULT_SUPPLEMENT_ICON,
      slot: s.slot,
      active: true,
    };
    persistStack(set, get, [...get().stack, full]);
    return full.id;
  },

  updateSupplement: (id, patch) => {
    persistStack(set, get, get().stack.map(s => (s.id === id ? { ...s, ...patch } : s)));
  },

  deleteSupplement: (id) => {
    persistStack(set, get, get().stack.filter(s => s.id !== id));
  },

  // ⭐ Pause and Resume keep their dates (utils/intakeSchedule `pauses`): a paused item's days
  // before its pause still count — never erased, never "not recorded" while it was paused.
  toggleActive: (id) => {
    const today = get().todayDate;
    persistStack(set, get, get().stack.map(s => (s.id !== id ? s : s.active ? pauseItem(s, today) : resumeItem(s, today))));
  },

  getSupplementXP: () => sumSupplementXP(get().history),
  // A day with nothing due (and no record) is passed over, not a break.
  getStreak: () => supplementStreak(get().history, get().todayDate, shiftYMD, date => dueCount(get().stack, date, [])),
  // Only the marks on today's counted doses count: a mark left on a dose since removed doesn't.
  getTodayAdherence: () => {
    const { todayTakenIds, todayScheduled } = get();
    const taken = get().getTodayDoses().filter(d => markOf(todayTakenIds, d.key) === 'taken').length;
    return { taken, scheduled: todayScheduled, pct: adherencePct(taken, todayScheduled) };
  },
  getTodayDoses: () => countedDoses(get().stack, get().todayDate, get().todayTakenIds, get().dayTimes),

  // Written whole (it's one small object, set in one dialog). ⚠️ Only after the save lands:
  // a failed save (no column yet — mig 0304 not run) leaves the day as it was and says so.
  setDayTimes: async (d) => {
    if (dayTimesError(d)) return false;
    const ok = await saveUserPreference({ intake_day_times: d });
    if (!ok) return false;
    const state = get();
    set({ dayTimes: d, dayTimesSaved: true });
    applyStack(set, get, state.stack);
    // every few hours from waking: a new day can move today's count — today's row says so too
    if (get().todayScheduled !== state.todayScheduled) void dayLog.rescore();
    return true;
  },

  clearStore: () => {
    dayLog.clear();
    stackSync.reset();
    set({
      stack: DEFAULT_STACK.map(s => ({ ...s })),
      stackSaved: false,
      todayDate: toLocalYMD(new Date()),
      todayTakenIds: [],
      todayScheduled: dayDoses(DEFAULT_STACK, toLocalYMD(new Date())).length, // with the day, reset below
      history: [],
      historyFrom: toLocalYMD(new Date()),
      recordStart: toLocalYMD(new Date()),
      dayTimes: DEFAULT_DAY_TIMES,
      dayTimesSaved: false,
      isLoading: false,
      loadFailed: false,
      isInitialized: false,
      initializedUserId: null,
    });
  },
}));

// The stack + a refreshed today-scheduled, in one set.
function applyStack(
  set: (partial: Partial<SupplementStore>) => void,
  get: () => SupplementStore,
  stack: Supplement[]
) {
  const state = get();
  const scheduled = dueCount(stack, state.todayDate, state.todayTakenIds);
  const todayDay: SupplementDay = { date: state.todayDate, takenIds: state.todayTakenIds, scheduled };
  set({ stack, todayScheduled: scheduled, history: withTodayInHistory(state.history, todayDay) });
}

// Optimistic local set of the stack, then persist (merged into the cloud's copy).
function persistStack(
  set: (partial: Partial<SupplementStore>) => void,
  get: () => SupplementStore,
  stack: Supplement[]
) {
  const was = get().todayScheduled;
  stackSync.recordBase(get().stack);
  applyStack(set, get, stack);
  // Every stack write is the person's (an add, an edit, a count, a pause): it is theirs from now on.
  set({ stackSaved: true });
  stackSync.schedule();
  // ⭐ Today's count moved (a Pause, a Resume, an item added or its days changed): today's row is
  // recounted too (lib/dayIdLog `rescore`), so tomorrow the day reads what it asked — never a dose
  // paused at noon as "not recorded" (polish round, fix 1).
  if (get().todayScheduled !== was) void dayLog.rescore();
}

/**
 * The day to read the record from: `normal` (60 days back), or earlier when a supply was
 * counted before that — never earlier than `floor`. Reads the saved list RAW (it isn't adopted
 * until the record is in).
 */
export function supplySince(saved: unknown, normal: string, floor: string): string {
  let since = normal;
  if (Array.isArray(saved)) {
    for (const item of saved as Partial<Supplement>[]) {
      const asOf = item?.supply?.asOf;
      if (typeof asOf === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(asOf) && asOf < since) since = asOf;
    }
  }
  return since < floor ? floor : since;
}

/**
 * ⭐ Where the record starts: the earliest saved day with something due (`scheduled > 0`) AND
 * something marked — else `today`, never after it (Supplements polish round, 2026-10-24): someone
 * new doesn't see weeks of "not recorded" before their first mark. A day whose marks were all
 * undone starts nothing (a tab left open past midnight says the same — `recordStartOn`). A day
 * before it is `'before'` — never a miss, never counted.
 */
export function recordStartOf(rows: readonly { log_date: string; scheduled: number | null; taken_ids: string[] | null }[], today: string): string {
  let start = today;
  for (const r of rows) if ((r.scheduled ?? 0) > 0 && (r.taken_ids ?? []).length > 0 && r.log_date < start) start = r.log_date;
  return start;
}

/**
 * The record's start for a load that read the days from `since` on (`rows`). The loaded rows
 * answer it — unless the record began BEFORE `since`: then ONE older row (something due, something
 * marked) says so, and the record starts at `since` (a day before it isn't loaded: no record to
 * show). Without that, someone with a long history but nothing saved on the window's first days
 * would read real misses as "before your record". A failed check keeps the loaded rows' answer.
 */
export async function loadRecordStart(
  table: 'supplement_logs' | 'medication_logs',
  userId: string,
  rows: readonly { log_date: string; scheduled: number | null; taken_ids: string[] | null }[],
  since: string,
  today: string,
): Promise<string> {
  const loaded = recordStartOf(rows, today);
  if (loaded <= since) return loaded;
  const older = await supabase
    .from(table)
    .select('log_date')
    .eq('user_id', userId)
    .gt('scheduled', 0)
    .neq('taken_ids', '{}')
    .lt('log_date', since)
    .limit(1);
  if (older.error) { console.error(`Error checking where the ${table} record starts:`, older.error); return loaded; }
  return (older.data ?? []).length > 0 ? since : loaded;
}

/**
 * The record's start once the day moves on to `date` (the tab stayed open past midnight). A day
 * that ends with NOTHING marked (never marked, or every mark undone) starts nothing, so a record
 * that would have started on it starts later — what a fresh load would say (`recordStartOf`).
 * Never after `date`.
 */
export function recordStartOn(s: { recordStart: string; todayDate: string; todayTakenIds: readonly string[]; todayScheduled: number }, date: string): string {
  const recordedThatDay = s.todayTakenIds.length > 0 && s.todayScheduled > 0;
  const start = date > s.todayDate && s.recordStart >= s.todayDate && !recordedThatDay ? date : s.recordStart;
  return start > date ? date : start;
}

/**
 * The person's day was SAVED: a whole day, every time valid and in order — what Your day writes
 * (`setDayTimes`). Missing (never set, or mig 0304 not run) or anything `readDayTimes` would
 * replace with a default is not saved: the defaults stand in, and "Set your day" stays.
 */
export function dayTimesWereSaved(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const read = readDayTimes(raw);
  return (Object.keys(read) as (keyof DayTimes)[]).every(k => (raw as Record<string, unknown>)[k] === read[k]);
}

onSignOut(() => useSupplementStore.getState().clearStore());
